import React from 'react';
import { View, ScrollView, StyleSheet } from 'react-native';
import { StatTile, type StatTileProps } from './StatTile';
import { useResponsive } from '@/hooks/common/useResponsive';

export type StatTileSpec = StatTileProps & { id: string };

export interface KPISummaryBarProps {
  tiles: StatTileSpec[];
  /**
   * `'auto'` (default) resolves at runtime via `useResponsive().layoutMode`:
   * desktop → grid; mobile/tablet → snap-scrolling row. Pass an explicit
   * value to override (e.g. force scroll on a small embedded surface).
   */
  layout?: 'auto' | 'scroll' | 'grid';
  testID?: string;
}

/**
 * Horizontal row of `StatTile` summaries used at the top of resident and
 * admin landing surfaces. Resolves layout responsively so handsets get a
 * snap-scrolling carousel and desktops get a flexed grid without the caller
 * having to branch.
 */
export const KPISummaryBar: React.FC<KPISummaryBarProps> = React.memo(
  ({ tiles, layout = 'auto', testID }) => {
    const { layoutMode } = useResponsive();
    const resolved =
      layout === 'auto' ? (layoutMode === 'desktop' ? 'grid' : 'scroll') : layout;

    if (resolved === 'scroll') {
      return (
        <ScrollView
          horizontal
          showsHorizontalScrollIndicator={false}
          contentContainerStyle={styles.scrollContent}
          decelerationRate="fast"
          snapToInterval={196}
          testID={testID}
        >
          {tiles.map((t) => (
            <View key={t.id} style={styles.scrollItem}>
              <StatTile {...t} />
            </View>
          ))}
        </ScrollView>
      );
    }

    return (
      <View style={styles.grid} testID={testID}>
        {tiles.map((t) => (
          <View key={t.id} style={styles.gridItem}>
            <StatTile {...t} />
          </View>
        ))}
      </View>
    );
  },
);

KPISummaryBar.displayName = 'KPISummaryBar';

const styles = StyleSheet.create({
  scrollContent: { paddingRight: 16, gap: 10 },
  scrollItem: { width: 184 },
  grid: { flexDirection: 'row', alignItems: 'stretch', flexWrap: 'wrap', gap: 12 },
  gridItem: { flex: 1, minWidth: 120, maxWidth: 240 },
});
